import api from '@Services/api';
import {
	AssignmentSubmission,
	SubmissionsMeta,
	TestSubmission,
} from '../../StandaloneCourses/Submissions/Submissions.types';

// Plain axios, no RTK: this page is shared source compiled under both apps,
// and the two apps' slice conventions differ. The api instance carries the
// portal prefix (/admin/ or /instructor/), so every path below resolves to
// the matching SubmissionsController.

export type ProductType = 'classroom' | 'standalone';

/** pending = awaiting review, graded = closed / scored, all = both. */
export type ReviewState = 'pending' | 'graded' | 'all';

export type SubmissionsFilters = {
	instructor_id?: number;
	product_type?: ProductType;
	product_id?: number;
	course_id?: number;
	unit_id?: number;
	/** Always sent: a boolean could not carry "all" on the wire. */
	state: ReviewState;
	from?: string;
	to?: string;
	query?: string;
};

export type ListResponse<T> = { data: T[]; meta: SubmissionsMeta };

const body = (filters: SubmissionsFilters, page: number, perPage = 10) => {
	// undefined keys are dropped by axios; `sortBy` is deliberately absent
	// (an empty `sortBy: {}` is a 422 on the backend).
	return {
		page,
		perPage,
		instructor_id: filters.instructor_id,
		product_type: filters.product_type,
		product_id: filters.product_id,
		course_id: filters.course_id,
		unit_id: filters.unit_id,
		state: filters.state,
		from: filters.from,
		to: filters.to,
		query: filters.query || undefined,
	};
};

export const fetchTestSubmissions = async (filters: SubmissionsFilters, page: number) => {
	const response = await api.post('submissions/tests', body(filters, page));
	return response.data as ListResponse<TestSubmission>;
};

export const fetchAssignmentSubmissions = async (filters: SubmissionsFilters, page: number) => {
	const response = await api.post('submissions/assignments', body(filters, page));
	return response.data as ListResponse<AssignmentSubmission>;
};

/**
 * The KPI chips: always the pending counts. `unitKind` says which list the
 * unit filter belongs to, so a test unit is not applied to the assignments
 * count (that read as "0 pending" rather than "not applicable").
 */
export const fetchPendingCounts = async (filters: Omit<SubmissionsFilters, 'state'>, unitKind: 'test' | 'assignment') => {
	const response = await api.post(
		`submissions/counts?unit_kind=${unitKind}`,
		body({ ...filters, state: 'pending', query: undefined }, 1)
	);
	return response.data.data as { tests: number; assignments: number };
};

export type BreakdownProduct = {
	product_type: ProductType;
	product_id: number;
	title: string;
	label?: string | null;
	pending_tests: number;
	pending_assignments: number;
};
export type BreakdownInstructor = {
	instructor_id: number;
	first_name?: string;
	last_name?: string;
	pending_tests: number;
	pending_assignments: number;
	products: BreakdownProduct[];
};
export type BreakdownResponse = {
	instructors: BreakdownInstructor[];
	totals: { pending_tests: number; pending_assignments: number };
};

/**
 * Pending counts grouped instructor → product (the Breakdown subtab). The
 * instructor portal gets the same shape with at most one instructor entry.
 * Products with nothing pending don't appear.
 */
export const fetchSubmissionsBreakdown = async (range: { from?: string; to?: string }) => {
	const response = await api.post('submissions/breakdown', {
		from: range.from,
		to: range.to,
	});
	return response.data.data as BreakdownResponse;
};

export type ProductOption = { id: number; title: string; label?: string; instructor_id?: number };
export type InstructorOption = { id: number; first_name: string; last_name: string };
export type CourseOption = { id: number; name: string };
export type UnitOption = { id: number; name: string; course_id: number };

/** No `type` = the instructors (admin); a `type` = that product family only. */
export const fetchProducts = async (instructorId?: number, type?: ProductType) => {
	const query = type ? `?type=${type}` : '';
	const response = await api.get(`submissions/products${instructorId ? `/${instructorId}` : ''}${query}`);
	return response.data.data as {
		instructors: InstructorOption[];
		classrooms: ProductOption[];
		standalone_courses: ProductOption[];
	};
};

export const fetchCourses = async (productType: ProductType, productId: number) => {
	const response = await api.get(`submissions/courses/${productType}/${productId}`);
	return response.data.data as CourseOption[];
};

export const fetchUnits = async (courseId: number, type: 'test' | 'assignment') => {
	const response = await api.get(`submissions/units/${courseId}?type=${type}`);
	return response.data.data as UnitOption[];
};
